/**
 * Icon Utilities
 * Maps icon name strings (from DB) to lucide-react components
 */
import * as LucideIcons from 'lucide-react';
import { getNavToModuleMap } from '../services/modulesService';

/**
 * Get a lucide-react icon component by its name
 * @param {string} iconName - The icon name (e.g., 'MessageCircle', 'Users', 'MapPin')
 * @param {React.Component} fallback - Fallback icon if not found (default: LayoutGrid)
 * @returns {React.Component} The lucide-react icon component
 */
export const getIconByName = (iconName, fallback = LucideIcons.LayoutGrid) => {
    if (!iconName) return fallback;

    // Special cases for specific modules if DB names don't match Lucide names
    const specialCases = {
        'TIME_TRACKING': LucideIcons.Clock,
        'timeTracking': LucideIcons.Clock,
        'timetracking': LucideIcons.Clock,
        'AGENDA': LucideIcons.Calendar,
    };

    if (specialCases[iconName]) {
        return specialCases[iconName];
    }

    // Try exact match first
    if (LucideIcons[iconName]) {
        return LucideIcons[iconName];
    }

    // Try with different cases (PascalCase, camelCase)
    const pascalCase = iconName.charAt(0).toUpperCase() + iconName.slice(1);
    if (LucideIcons[pascalCase]) {
        return LucideIcons[pascalCase];
    }

    return fallback;
};

/**
 * Get module icon and color from module data
 * @param {Object} module - Module object from DB/localStorage
 * @returns {{ Icon: React.Component, color: string }}
 */
export const getModuleIconAndColor = (module) => {
    const iconName = module?.icon || module?.ICON;
    const color = module?.color || module?.COLOR || '#8b5cf6';
    const Icon = getIconByName(iconName);

    return { Icon, color };
};

/**
 * Find module by navId from modules list
 * @param {string} navId - Navigation ID (e.g., 'messages', 'contacts', 'timeTracking')
 * @param {Array} modulesList - List of modules from localStorage
 * @returns {Object|null} Module object or null
 */
export const findModuleByNavId = (navId, modulesList) => {
    if (!modulesList || !navId) return null;

    // Get the module code from navId using the dynamic map
    const navToModuleMap = getNavToModuleMap();
    const moduleCode = navToModuleMap[navId];

    if (moduleCode) {
        // Find by code
        return modulesList.find(m => {
            const code = (m.code || m.CODE || '').toUpperCase();
            return code === moduleCode;
        }) || null;
    }

    // Fallback: try direct match on code (for cases where navId === code.toLowerCase())
    const upperNavId = navId.toUpperCase();
    return modulesList.find(m => {
        const code = (m.code || m.CODE || '').toUpperCase();
        return code === upperNavId;
    }) || null;
};

/**
 * Find module by code from modules list
 * @param {string} code - Module code (e.g., 'MESSAGES', 'CONTACTS')
 * @param {Array} modulesList - List of modules from localStorage
 * @returns {Object|null} Module object or null
 */
export const findModuleByCode = (code, modulesList) => {
    if (!modulesList || !code) return null;

    const upperCode = code.toUpperCase();
    return modulesList.find(m => {
        const moduleCode = (m.code || m.CODE || '').toUpperCase();
        return moduleCode === upperCode;
    }) || null;
};
